import dayjs from 'dayjs';
import { OpUnitType, QUnitType, Dayjs, ManipulateType } from 'dayjs';

// plugins for dayjs
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';


// importing locales for multilanguage support
import 'dayjs/locale/es';

// Importing date formats from dateFormat Utils
import { MM_DD_YYYY } from './dateFormats.utils';

// plugin extension for dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

export type setDateType = {
    year?: number;
    month?: number;
    date?: number;
    hour?: number;
    minute?: number;
    second?: number;
};

/**
 * Returns a dayjs instance.
 * @example
 * dayjsUtil(); // Dayjs object
 */
export const dayjsUtil = () => {
    return dayjs();
};

/**
 * Constructs a Day.js instance from an object of type setDateType.
 * Rejects incomplete values and calendar overflows (e.g. Feb 30 → Mar 1).
 * @param {setDateType} obj - An object containing date values. `month` is 0-based.
 * @returns {Dayjs | null} - The constructed Day.js object, or null if invalid.
 * @example
 * constructDate({ year: 2024, month: 11, date: 21 }); // Returns a Day.js object for December 21, 2024
 * constructDate({ year: 2000, month: 1, date: 30 }); // Returns null (Feb 30 is invalid)
 */
export const constructDate = (obj: setDateType): Dayjs | null => {
    const { date, month, year } = obj || {};
    // month === -1 when DatePicker passes an empty month via (+month - 1)
    if (!date || month === undefined || month === null || month < 0 || !year) {
        return null;
    }

    const constructed = dayjs({ date, month, year });

    // dayjs overflows invalid calendar dates (Feb 30 → Mar 1). Reject those.
    if (
        !constructed.isValid() ||
        constructed.year() !== year ||
        constructed.month() !== month ||
        constructed.date() !== date
    ) {
        return null;
    }

    return constructed;
};

/**
 * Parses a date string, Date object, number (timestamp), or Day.js object into a Day.js instance.
 * @param {string | Date | Dayjs | number} date - The date to parse.
 * @returns {Dayjs} - The parsed Day.js object.
 * @example
 * parseDate('2024-12-12'); // Returns Day.js object for December 12, 2024
 * parseDate(new Date()); // Returns Day.js object for the current date
 * parseDate(1700000000000); // Parses a timestamp
 */
export const parseDate = (date: string | Date | Dayjs | number): Dayjs => {
    return dayjs(date);
};

/**
 * Parses a date string, Date object, or Day.js object into a Day.js instance with a specified format.
 * @param {string | Date | Dayjs} date - The date to parse.
 * @param {string} format - The format to parse the date with.
 * @returns {dayjs.Dayjs} - The parsed Day.js object.
 * @example
 * parseDateWithFormat('21/12/2024', 'DD/MM/YYYY'); // Returns Day.js object for December 21, 2024
 */
export const parseDateWithFormat = (
    date: string | Date | Dayjs,
    format: string,
): Dayjs => {
    return dayjs(date, format);
};

/**
 * Parses a date string, Date object, or Day.js object into a Day.js instance with a specified format.
 * @param {string | Date | Dayjs} date - The date to parse.
 * @param {string} format - The format to parse the date with.
 * @returns {dayjs.Dayjs} - The parsed Day.js object without conversion to local timezone.
 * @example
 * parseUTCDateWithFormat('21/12/2024', 'DD/MM/YYYY'); // Returns Day.js object for December 21, 2024
 */
export const parseUTCDateWithFormat = (
    date: string | Date | Dayjs,
    format: string,
): Dayjs => {
    return dayjs.utc(date, format);
};


/**
 * Returns the current date and time formatted according to the given format.
 * @param {string} [format=''] - The format string (optional).
 * @returns {string} - The formatted current date.
 * @example
 * nowUtil('YYYY-MM-DD'); // "2024-12-12"
 */
export const nowUtil = (format: string = ''): string => {
    return dayjs().format(format);
};

/**
 * Returns the current date and time in UTC.
 * @returns {dayjs.Dayjs} - The current UTC time as a Day.js object.
 * @example
 * getDateInUtc(); // Returns current date and time in UTC
 */
export const getDateInUtc = (): Dayjs => {
    return dayjs.utc();
};

/**
 * Returns the current UTC date and time in a specified time zone.
 * @param {string} zone - The time zone identifier (e.g., "America/New_York").
 * @returns {dayjs.Dayjs} - The UTC date and time adjusted to the specified time zone.
 * @example
 * getDateUtcInTimeZone('Asia/Kolkata'); // Returns current UTC date and time in IST
 */
export const getDateUtcInTimeZone = (zone: string): Dayjs => {
    return dayjs.utc().tz(zone);
};

/**
 * Parses a given date and converts it to UTC mode.
 * @param {string | Date | Dayjs | number} date - The date to be parsed as UTC format. Can be a string, JavaScript Date object, Dayjs instance, or epoch ms.
 * @returns {dayjs.Dayjs} - The parsed date in UTC.
 * @example
 * parseDateInUtc("2025-03-11T14:23:45Z"); // Returns the given date in UTC
 */
export const parseDateInUtc = (date: string | Date | Dayjs | number) => {
    return dayjs.utc(date);
};

/**
 * Converts a date to UTC.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @returns {dayjs.Dayjs} - The date converted to UTC.
 * @example
 * convertDateToUtc('2024-12-12'); // Converts date to UTC
 */
export const convertDateToUtc = (date: string | Date | Dayjs): Dayjs => {
    return dayjs(date).utc();
};

/**
 * Converts a date to UTC and adjusts it to a specified time zone.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @param {string} zone - The time zone identifier (e.g., "America/New_York").
 * @returns {dayjs.Dayjs} - The date converted to UTC and adjusted to the specified time zone.
 * @example
 * convertDateToUtcAndTimeZone('2024-12-12', 'Asia/Kolkata'); // Converts to UTC and adjusts to IST
 */
export const convertDateToUtcAndTimeZone = (
    date: string | Date | Dayjs,
    zone: string,
): Dayjs => {
    return dayjs(date).utc().tz(zone);
};

/**
 * Parses a date as UTC and formats it according to the given format.
 * Date-only strings (YYYY-MM-DD) are treated as UTC calendar dates so they
 * do not shift by a day in timezones east of UTC. Epoch-ms timestamps are
 * formatted as the UTC calendar day of that instant.
 * @param {string | Date | Dayjs | number} date - The date to convert. Can also be an epoch-ms timestamp.
 * @param {string} format - The format string for the date.
 * @returns {string} - The formatted date in UTC.
 * @example
 * toUTCAndFormat('2024-12-12', 'YYYY-MM-DD'); // "2024-12-12"
 */
export const toUTCAndFormat = (date: string | Date | Dayjs | number, format: string): string => {
    return parseDateInUtc(date).format(format);
};

/**
 * Returns the current UTC date and time formatted according to the given format.
 * @param {string} [format=''] - The format string (optional).
 * @returns {string} - The formatted UTC date.
 * @example
 * getFormatedDateInUtc('YYYY-MM-DD'); // "2024-12-12"
 */
export const getFormatedDateInUtc = (format: string = ''): string => {
    return dayjs().utc().format(format);
};

/**
 * Converts a date to a specified time zone.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @param {string} zone - The time zone identifier (e.g., "America/New_York").
 * @returns {dayjs.Dayjs} - The date adjusted to the specified time zone.
 * @example
 * convertDateToTimeZone('2024-12-12', 'Asia/Kolkata'); // Converts date to IST
 */
export const convertDateToTimeZone = (
    date: string | Date | Dayjs,
    zone: string,
): Dayjs => {
    return dayjs.tz(date, zone);
};

/**
 * Converts a date to the specified time zone and formats it.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @param {string} timezone - The timezone to convert the date to.
 * @param {string} [format='MM/DD/YYYY'] - The format string (optional).
 * @returns {string} - The formatted date in the specified timezone.
 * @example
 * formatDateToTimezone('2024-12-12', 'America/New_York', 'YYYY-MM-DD'); // "2024-12-12"
 */
export const formatDateToTimezone = (
    date: string | Date | Dayjs,
    timezone: string,
    format: string = MM_DD_YYYY,
): string => {
    return dayjs(date).tz(timezone).format(format);
};

/**
 * Guesses the user's current timezone.
 * @returns {string} - The guessed timezone identifier.
 * @example
 * guessTimeZone(); // "Asia/Kolkata"
 */
export const guessTimeZone = (): string => {
    return dayjs.tz.guess();
};

/**
 * Formats a given date according to the specified format string.
 * @param {string | Date | Dayjs} date - The date to format.
 * @param {string} format - The format string.
 * @returns {string} - The formatted date.
 * @example
 * formatDate('2024-12-12', 'DD/MM/YYYY'); // "12/12/2024"
 */
export const formatDate = (
    date: string | Date | Dayjs,
    format: string,
): string => {
    return dayjs(date).format(format);
};

/**
 * Returns the previous or adjusted date by subtracting the specified count of units.
 * @param {number} count - The number of units to subtract.
 * @param {ManipulateType} parameter - The unit of time to subtract (e.g., 'year', 'month').
 * @param {string | Date | Dayjs | null} [currentDate=null] - The date to start from (optional).
 * @returns {Dayjs} - The adjusted date as a Day.js object.
 * @example
 * subtractFromDate(1, 'year', '2025-01-01'); // Day.js object for "2024-01-01"
 */
export const subtractFromDate = (
    count: number,
    parameter: ManipulateType,
    currentDate: string | Date | Dayjs | null = null,
): Dayjs => {
    return currentDate
        ? dayjs(currentDate).subtract(count, parameter)
        : dayjs().subtract(count, parameter);
};

/**
 * Returns the adjusted date in a specified format after subtracting the given count of units.
 * @param {number} count - The number of units to subtract.
 * @param {ManipulateType} parameter - The unit of time to subtract (e.g., 'year', 'month').
 * @param {string} [format=''] - The desired format for the output (optional).
 * @param {string | Date | Dayjs | null} [currentDate=null] - The date to start from (optional).
 * @returns {string} - The formatted adjusted date.
 * @example
 * subtractFromDateWithFormat(1, 'year', 'YYYY-MM-DD', '2025-01-01'); // "2024-01-01"
 */
export const subtractFromDateWithFormat = (
    count: number,
    parameter: ManipulateType,
    format: string = '',
    currentDate: string | Date | Dayjs | null = null,
): string => {
    return currentDate
        ? dayjs(currentDate).subtract(count, parameter).local().format(format)
        : dayjs().subtract(count, parameter).local().format(format);
};

/**
 * Adds the specified count of units to the given date.
 * @param {number} count - The number of units to add.
 * @param {ManipulateType} parameter - The unit of time to add (e.g., 'year', 'month').
 * @param {string | Date | Dayjs | null} [currentDate=null] - The date to start from (optional).
 * @returns {Dayjs} - The adjusted date as a Day.js object.
 * @example
 * addToDate(1, 'month', '2025-01-01'); // Day.js object for "2025-02-01"
 */
export const addToDate = (
    count: number,
    parameter: ManipulateType,
    currentDate: string | Date | Dayjs | null = null,
): Dayjs => {
    return currentDate
        ? dayjs(currentDate).add(count, parameter)
        : dayjs().add(count, parameter);
};

/**
 * Returns the adjusted date in a specified format after adding the given count of units.
 * @param {number} count - The number of units to add.
 * @param {ManipulateType} parameter - The unit of time to add (e.g., 'year', 'month').
 * @param {string} [format=''] - The desired format for the output (optional).
 * @param {string | Date | Dayjs | null} [currentDate=null] - The date to start from (optional).
 * @returns {string} - The formatted adjusted date.
 * @example
 * addToDateWithFormat(1, 'month', 'YYYY-MM-DD', '2025-01-01'); // "2025-02-01"
 */
export const addToDateWithFormat = (
    count: number,
    parameter: ManipulateType,
    format: string = '',
    currentDate: string | Date | Dayjs | null = null,
): string => {
    return currentDate
        ? dayjs(currentDate).add(count, parameter).local().format(format)
        : dayjs().add(count, parameter).local().format(format);
};

/**
 * Sets the specified date and time components on the given date.
 * @param {setDateType} params - The date and time parameters to set.
 * @param {string | Dayjs | Date} [currentDate=dayjs()] - The date to update (optional).
 * @returns {Dayjs} - The Day.js object with the updated date and time.
 * @example
 * setDate({ year: 2024, month: 11, date: 25, hour: 12, minute: 30, second: 0 });
 * // Returns Day.js object for December 25, 2024, 12:30:00
 */
export const setDate = (
    params: setDateType,
    currentDate: string | Dayjs | Date = dayjs(),
): Dayjs => {
    const { year, month, date, hour, minute, second } = params;
    let finalDate = dayjs(currentDate);
    if (second !== undefined) finalDate = finalDate.set('second', second);
    if (minute !== undefined) finalDate = finalDate.set('minute', minute);
    if (hour !== undefined) finalDate = finalDate.set('hour', hour);
    if (date !== undefined) finalDate = finalDate.set('date', date);
    if (month !== undefined) finalDate = finalDate.set('month', month);
    if (year !== undefined) finalDate = finalDate.set('year', year);
    return finalDate;
};

/**
 * Returns the ISO string representation of the given date.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @returns {string} - The ISO string of the date.
 * @example
 * toISOStringUtil('2024-12-12'); // "2024-12-12T00:00:00.000Z"
 */
export const toISOStringUtil = (date: string | Date | Dayjs): string => {
    return dayjs(date).toISOString();
};

/**
 * Converts the given date into a native JavaScript Date object.
 * @param {string | Date | Dayjs} date - The date to convert.
 * @returns {Date} - The native JavaScript Date object.
 * @example
 * getDateObject('2024-12-12'); // Returns a Date object for December 12, 2024
 */
export const getDateObject = (date: string | Date | Dayjs): Date => {
    return dayjs(date).toDate();
};

/**
 * Returns the day of the month for the given date.
 * @param {string | Date | Dayjs} date - The date to extract the day from.
 * @returns {number} - The day of the month.
 * @example
 * getDateOfMonth('2024-12-12'); // 12
 */
export const getDateOfMonth = (date: string | Date | Dayjs): number => {
    return dayjs(date).date();
};

/**
 * Returns the month (zero-based) for the given date.
 * @param {string | Date | Dayjs} date - The date to extract the month from.
 * @returns {number} - The month (0-11).
 * @example
 * getMonthInNumber('2024-12-12'); // 11 (December)
 */
export const getMonthInNumber = (date: string | Date | Dayjs): number => {
    return dayjs(date).month();
};

/**
 * Returns the year for the given date.
 * @param {string | Date | Dayjs} date - The date to extract the year from.
 * @returns {number} - The year of the given date.
 * @example
 * getYearInNumber('2024-12-12'); // 2024
 */
export const getYearInNumber = (date: string | Date | Dayjs): number => {
    return dayjs(date).year();
};

/**
 * Returns a Dayjs object representing the given year.
 * @param {number} year - The year to set.
 * @returns {Dayjs} - The Dayjs object for the specified year.
 * @example
 * getDateFromYear(2024); // Returns Dayjs object for the year 2024
 */
export const getDateFromYear = (year: number): Dayjs => {
    return dayjs().year(year);
};

/**
 * Returns a Dayjs object representing the given month.
 * @param {number} month - The month (0-based) to set.
 * @returns {Dayjs} - The Dayjs object for the specified month.
 * @example
 * getDateFromMonth(11); // Returns Dayjs object for December (0-based month)
 */
export const getDateFromMonth = (month: number): Dayjs => {
    return dayjs().month(month);
};

/**
 * Returns a Dayjs object representing the given date.
 * @param {number} date - The day of the month to set.
 * @returns {Dayjs} - The Dayjs object for the specified date.
 * @example
 * getDateFromDate(12); // Returns Dayjs object for the 12th day of the current month
 */
export const getDateFromDate = (date: number): Dayjs => {
    return dayjs().date(date);
};

/**
 * Returns the time in milliseconds for the given date.
 * @param {string | Date | Dayjs} date - The date to convert to milliseconds.
 * @returns {number} - The time in milliseconds since the Unix epoch.
 * @example
 * getDateTimeInMillisecond('2024-12-12'); // 1713033600000
 */
export const getDateTimeInMillisecond = (
    date: string | Date | Dayjs,
): number => {
    return dayjs(date).valueOf();
};

/**
 * Concatenates a date string with a specified year and formats it.
 * @param {string} dateAndMonth - The date and month part (e.g., '12/25/').
 * @param {string} year - The year to append (e.g., '2024').
 * @param {string} [format=''] - The format to apply to the concatenated result (optional).
 * @returns {string} - The formatted concatenated date (e.g., "12/25/2024").
 * @example
 * addYearToDateAndMonth('12/25/', '2024', 'MM/DD/YYYY'); // "12/25/2024"
 */
export const addYearToDateAndMonth = (
    dateAndMonth: string,
    year: string,
    format: string = '',
): string => {
    return dayjs(dateAndMonth + year).format(format);
};

/**
 * Checks if the first date is the same or after the second date.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @returns {boolean} - True if the first date is the same or after the second date.
 * @example
 * isDateSameOrAfter('2024-12-12', '2024-12-11'); // true
 */
export const isDateSameOrAfter = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit?: OpUnitType,
): boolean => {
    return unit
        ? dayjs(firstDate).isSameOrAfter(dayjs(secondDate), unit)
        : dayjs(firstDate).isSameOrAfter(dayjs(secondDate));
};

/**
 * Checks if the first date is after the second date.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @returns {boolean} - True if the first date is after the second date.
 * @example
 * isDateAfter('2024-12-12', '2024-12-11'); // true
 */
export const isDateAfter = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit?: OpUnitType,
) => {
    return unit
        ? dayjs(firstDate).isAfter(dayjs(secondDate), unit)
        : dayjs(firstDate).isAfter(dayjs(secondDate));
};

/**
 * Checks if the first date is the same or before the second date.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @returns {boolean} - True if the first date is the same or before the second date.
 * @example
 * isDateSameOrBefore('2024-12-12', '2024-12-13'); // true
 */
export const isDateSameOrBefore = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit?: OpUnitType,
): boolean => {
    return unit
        ? dayjs(firstDate).isSameOrBefore(dayjs(secondDate), unit)
        : dayjs(firstDate).isSameOrBefore(dayjs(secondDate));
};

/**
 * Checks if the first date is before the second date.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @returns {boolean} - True if the first date is before the second date.
 * @example
 * isDateBefore('2024-12-12', '2024-12-13'); // true
 */
export const isDateBefore = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit: OpUnitType | null = null,
) => {
    return unit
        ? dayjs(firstDate).isBefore(dayjs(secondDate), unit)
        : dayjs(firstDate).isBefore(dayjs(secondDate));
};

/**
 * Checks if a date is between two other dates.
 * @param {string | Date} mainDate - The date to check.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @param {'()' | '[]' | '[)' | '(]'} [inclusivity] - The inclusivity rule (optional).
 * @returns {boolean} - True if the date is between the two other dates.
 * @example
 * isDateBetween('2024-12-12', '2024-12-01', '2024-12-15'); // true
 */
export const isDateBetween = (
    mainDate: string | Date | Dayjs,
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit: OpUnitType | null = null,
    inclusivity: '()' | '[]' | '[)' | '(]' = '[]',
) => {
    return dayjs(mainDate).isBetween(
        dayjs(firstDate),
        dayjs(secondDate),
        unit,
        inclusivity,
    );
};

/**
 * Checks if the first date is the same as the second date.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {OpUnitType} [unit] - The unit of time to compare (optional).
 * @returns {boolean} - True if the first date is the same as the second date.
 * @example
 * isDateSame('2024-12-12', '2024-12-12'); // true
 */
export const isDateSame = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit: OpUnitType | null = null,
): boolean => {
    return unit
        ? dayjs(firstDate).isSame(secondDate, unit)
        : dayjs(firstDate).isSame(secondDate);
};

/**
 * Returns the difference between two dates in the specified unit.
 * @param {string | Date} firstDate - The first date to compare.
 * @param {string | Date} secondDate - The second date to compare.
 * @param {QUnitType} [unit='year'] - The unit of time to measure the difference (optional).
 * @param {boolean} [isFloat=false] - Whether to return a floating-point number (optional).
 * @returns {number} - The difference between the two dates in the specified unit.
 * @example
 * getDateDifference('2024-12-12', '2024-12-01', 'days'); // 11
 */
export const getDateDifference = (
    firstDate: string | Date | Dayjs,
    secondDate: string | Date | Dayjs,
    unit: QUnitType | OpUnitType = 'year',
    isFloat: boolean = false,
): number => {
    return dayjs(firstDate).diff(dayjs(secondDate), unit, isFloat);
};

/**
 * Checks if the given date is valid.
 * @param {string | Date} date - The date to check.
 * @returns {boolean} - True if the date is valid.
 * @example
 * isDateValid('2024-12-12'); // true
 */
export const isDateValid = (
    date: string | Date | Dayjs,
    format: string = '',
): boolean => {
    return dayjs(date, format, true).isValid();
};

/**
 * Returns the formatted date based on the provided locale and format.
 * @param {string | Date} date - The date to format.
 * @param {string} locale - The locale to use for formatting.
 * @param {string} format - The format to apply to the date.
 * @returns {string} - The formatted date string.
 * @example
 * getFormattedLocaleDate('2024-12-12', 'en', 'YYYY-MM-DD'); // "2024-12-12"
 */
export const getFormattedLocaleDate = (
    date: string | Date | Dayjs,
    locale: string,
    format: string,
): string => {
    return dayjs(date).locale(locale).format(format);
};

/**
 * Returns the start of a given date for the specified unit (day, month, week, year).
 * @param {string | Date} date - The date to adjust.
 * @param {'day' | 'month' | 'week' | 'year'} unit - The unit of time to set the start of.
 * @param {string | null} [format=null] - Optional format for the date string (optional).
 * @returns {Dayjs} - The adjusted date representing the start of the specified unit.
 * @example
 * startOf('2024-12-12', 'month'); // Returns the start of December 2024
 */
export const startOf = (
    date: string | Date | Dayjs,
    unit: 'day' | 'month' | 'week' | 'year',
    format: string | null = null,
): Dayjs => {
    return format
        ? dayjs(date, format).startOf(unit)
        : dayjs(date).startOf(unit);
};

/**
 * Returns the end of a given date for the specified unit (day, month, week, year).
 * @param {string | Date} date - The date to adjust.
 * @param {'day' | 'month' | 'week' | 'year'} unit - The unit of time to set the end of.
 * @param {string | null} [format=null] - Optional format for the date string (optional).
 * @returns {Dayjs} - The adjusted date representing the end of the specified unit.
 * @example
 * endOf('2024-12-12', 'month'); // Returns the end of December 2024
 */
export const endOf = (
    date: string | Date | Dayjs,
    unit: 'day' | 'month' | 'week' | 'year',
    format: string | null = null,
): Dayjs => {
    return format ? dayjs(date, format).endOf(unit) : dayjs(date).endOf(unit);
};
